import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Button, TextField } from '@mui/material';
import ReactLoading from 'react-loading';

import Select from '../../../ui/Select';
import { submitDisputeAction } from '../../../modules/reports/thunks';

const MUTUAL_RESOLUTION = 'Mutual Resolution';
const DisputesAction = ({ houseRef, selectedRefs }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const messageRef = useRef('');

  const [reasonID, setReasonID] = useState('');
  const [showMutualResolution, setShowMutualResolution] = useState(false);
  const [error, setError] = useState('');

  const { disputeTypes = [], mutualResolution = [] } = useSelector(state => state.reports.disputeTypes) || {};
  const disputeTypesLoading = useSelector(state => state.reports.disputeTypesLoading);
  const submitActionLoading = useSelector(state => state.reports.submitActionLoading);
  const { groupedData = [] } = useSelector(state => state.reports.sessionLots);

  const getMenus = data => data.map(({ disputeTypeName, disputeTypeID }) => ({
    value: disputeTypeID.toString(),
    label: disputeTypeName
  }));

  const { disputesMenu = [], resolutionMenu = [] } = useMemo(
    () => ({ disputesMenu: getMenus(disputeTypes), resolutionMenu: getMenus(mutualResolution) }),
    [disputeTypes, mutualResolution]
  );

  useEffect(() => {
    const [firstMenu = {}] = disputesMenu || [];
    setReasonID(firstMenu.value || '4');
  }, [disputesMenu]);

  useEffect(() => {
    if (showMutualResolution) {
      const [firstMenu = {}] = resolutionMenu || [];
      setReasonID(firstMenu.value || '');
    }
  }, [resolutionMenu, showMutualResolution]);

  const submitDisputeHandler = () => {
    const message = messageRef.current.value;
    if (!message) {
      return setError('Message is required!');
    }
    dispatch(
      submitDisputeAction(houseRef, {
        action: 'open',
        houseUserRef: houseRef,
        transactionIDs: (groupedData.flatMap(({ items }) => items.map(({ transactionID }) => transactionID)) || []),
        message,
        reasonID
      })
    );
  };

  const changeDisputeTypeHandler = ({ value, label }) => {
    setReasonID(value);
    if (label === MUTUAL_RESOLUTION) {
      setShowMutualResolution(true);
    } else setShowMutualResolution(false);
  };

  return (
    <Box mt={2} mb={4}>
      {disputeTypesLoading ? (
        <ReactLoading className="mt-2" type="spin" height="auto" width={30} color="black" />
      ) : (
        <Box>
          <Select
            sx={{ width: 350, mt: 2 }}
            size="small"
            defaultValue={disputesMenu[0]?.value ?? ''}
            onChange={changeDisputeTypeHandler}
            label={(
              <Typography variant="body2" fontWeight="bold">
                Reason*
              </Typography>
            )}
            menus={disputesMenu}
          />
          {showMutualResolution && (
            <Box mt={3}>
              <Select
                defaultValue={resolutionMenu[0]?.value ?? ''}
                sx={{ width: 350 }}
                size="small"
                onChange={({ value }) => setReasonID(value)}
                label={(
                  <Typography variant="body2" fontWeight="bold">
                    Resolution Details*
                  </Typography>
                )}
                menus={resolutionMenu}
              />
            </Box>
          )}
        </Box>
      )}
      <Box width="500px" mt={3}>
        <Typography variant="body2" fontWeight="bold" mb={1}>
          Message:*
        </Typography>
        <TextField
          placeholder="Please write your message here."
          inputRef={messageRef}
          className="textarea"
          id="outlined-textarea"
          multiline
          rows={5}
          InputLabelProps={{
            shrink: true
          }}
          variant="standard"
        />
        <Typography variant="body2" color="red" mt={1}>
          {error}
        </Typography>
      </Box>
      <Box mt={4}>
        <Button
          disabled={submitActionLoading || disputeTypesLoading}
          variant="contained"
          color="primary"
          onClick={submitDisputeHandler}
        >
          Submit
          {submitActionLoading && (
            <ReactLoading className="ml-1" type="spin" height="auto" width={15} />
          )}
        </Button>
        <Button
          variant="text"
          color="secondary"
          sx={{ ml: 3 }}
          onClick={() => history.push('/reports/sessionResults/viewSessionResults', selectedRefs ? { selectedRefs } : undefined)}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default DisputesAction;
